// ABOUTME: Improved multi-file upload component with better architecture
// ABOUTME: Uses custom hooks, memoization, and follows accessibility best practices

import React, { useCallback, useMemo, useRef, memo, useEffect } from 'react';
import { Box, Button, Flex, VStack, VisuallyHiddenInput, useMultiStyleConfig, Center } from '@chakra-ui/react';
import { faCloudUpload } from '@fortawesome/pro-regular-svg-icons';
import { FileUploadProps, UploadStats } from './types/types';
import { FILE_UPLOAD_CONSTANTS, FILE_STATUS } from './utils/constants';
import { useFileValidation, useDragDrop, useFileUploadManager } from './hooks';
import { Icon } from '../../atoms/Icon';
import FileListItem from './FileListItem';
import { CustomFormControl } from '../../formInputs/CustomFormControl';

const FileUpload = memo(
  ({
    id = 'file-upload',
    files = [],
    onFileSelect,
    onFileRemove,
    maxFiles = FILE_UPLOAD_CONSTANTS.DEFAULT_MAX_FILES,
    maxFileSize = FILE_UPLOAD_CONSTANTS.MAX_FILE_SIZE,
    minFileSize = FILE_UPLOAD_CONSTANTS.MIN_FILE_SIZE,
    acceptedFileTypes = FILE_UPLOAD_CONSTANTS.DEFAULT_ACCEPTED_TYPES,
    label = FILE_UPLOAD_CONSTANTS.DEFAULT_LABEL,
    helpText,
    errorText,
    isOptional = true,
    isDisabled = false,
    showFileSize = true, // showUploadSpeed is deprecated and ignored
    allowMultiple = true,
    allowDragDrop = true,
    allowRetry = true,
    onFilesValidated,
    onUploadComplete,
    onAllUploadsComplete,
    onRetry,
    size = 'md',
    variant = 'pending',
  }: Omit<FileUploadProps, 'showUploadSpeed'> & { showUploadSpeed?: boolean }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Use FileUpload theme configuration
    const styles = useMultiStyleConfig('FileUpload', { size, variant });

    const { validateFiles } = useFileValidation({
      maxFileSize,
      minFileSize,
      acceptedFileTypes,
    });

    const uploadedFilesRef = useRef<Set<string>>(new Set());

    const uploadState = useMemo(() => {
      const hasUploading = files.some(f => f.status === FILE_STATUS.UPLOADING);
      const hasErrors = files.some(f => f.status === FILE_STATUS.ERROR);
      const allCompleted =
        files.length > 0 &&
        files.every(
          f =>
            f.status === FILE_STATUS.UPLOADED || f.status === FILE_STATUS.ERROR || f.status === FILE_STATUS.CANCELLED,
        );

      return {
        hasUploading,
        hasErrors,
        allCompleted,
        isActive: hasUploading,
      };
    }, [files]);

    const { retryUpload } = useFileUploadManager(files, {
      maxFiles,
      onStatsChange: useCallback(
        (newStats: UploadStats) => {
          if (newStats.uploadedFiles === newStats.totalFiles && newStats.totalFiles > 0) {
            if (onAllUploadsComplete) {
              onAllUploadsComplete();
            }
          }
        },
        [onAllUploadsComplete],
      ),
    });

    // Determine if more files can be added based on current props (not internal hook state)
    const canAddFiles = useMemo(() => (files?.length || 0) < maxFiles, [files?.length, maxFiles]);

    useEffect(() => {
      for (const file of files) {
        if (file.status === FILE_STATUS.UPLOADED && !uploadedFilesRef.current.has(file.id)) {
          uploadedFilesRef.current.add(file.id);
          if (onUploadComplete) {
            onUploadComplete(file.id);
          }
        }
      }
    }, [files, onUploadComplete]);

    const handleFileSelection = useCallback(
      (selectedFiles: File[]) => {
        if (!canAddFiles) {
          // Inform consumer via invalidFiles so it can surface a toast in app context.
          if (onFilesValidated) {
            const maxErr = FILE_UPLOAD_CONSTANTS.ERRORS.MAX_FILES_EXCEEDED(maxFiles);
            const invalids = selectedFiles.map(file => ({ file, error: maxErr }));
            onFilesValidated([], invalids);
          }
          return;
        }

        const { validFiles, invalidFiles } = validateFiles(selectedFiles);

        // Enforce the total maxFiles limit even when multiple files are selected at once
        const availableSlots = Math.max(0, maxFiles - (files?.length || 0));
        const filesToAdd = validFiles.slice(0, availableSlots);
        const overflowFiles = validFiles.slice(availableSlots);
        const overflowInvalids = overflowFiles.map(file => ({
          file,
          error: FILE_UPLOAD_CONSTANTS.ERRORS.MAX_FILES_EXCEEDED(maxFiles),
        }));

        // Add allowable files
        if (filesToAdd.length > 0) {
          onFileSelect(filesToAdd);
        }

        // Always notify consumer of validation outcome (including invalid-only or overflow cases)
        if (onFilesValidated) {
          onFilesValidated(validFiles, [...invalidFiles, ...overflowInvalids]);
        }
      },
      [canAddFiles, validateFiles, onFileSelect, onFilesValidated, maxFiles, files?.length],
    );

    const { isDragging, getDropZoneProps } = useDragDrop({
      isDisabled: isDisabled || !allowDragDrop,
      onDrop: handleFileSelection,
      acceptedFileTypes,
    });

    // Border color should be red when an explicit error is present (e.g., required or submit-blocked)
    // otherwise reflect upload/drag state
    const getContainerBorderColor = useCallback(
      (isInvalid: boolean) => {
        if (isInvalid) return 'file-status.error-border'; // Red for validation errors
        if (uploadState.hasUploading) return 'file-upload.dropzone-border-active'; // Orange for active uploads
        if (uploadState.hasErrors && !uploadState.hasUploading) return 'file-status.error-border'; // Red for errors
        if (isDragging) return 'file-upload.dropzone-border-active'; // Blue for drag state
        return 'file-upload.dropzone-border'; // Default gray
      },
      [uploadState, isDragging],
    );

    const handleFileInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const fileList = e.target.files;
        if (!fileList || fileList.length === 0) return;

        const filesArray = Array.from(fileList);
        handleFileSelection(filesArray);

        // Reset input value to allow selecting the same file again
        e.target.value = '';
      },
      [handleFileSelection],
    );

    const dropZoneProps = useMemo(() => {
      return {
        ...getDropZoneProps(),
      };
    }, [getDropZoneProps]);

    const fileList = useMemo(
      () => (
        <VStack spacing={2} align="stretch">
          {files.map(file => (
            <FileListItem
              key={file.id}
              file={file}
              onRemove={onFileRemove}
              onRetry={allowRetry ? onRetry || retryUpload : undefined}
              isDisabled={isDisabled}
              showFileSize={showFileSize}
              allowRetry={allowRetry}
              size={size}
            />
          ))}
        </VStack>
      ),
      [files, onFileRemove, retryUpload, onRetry, isDisabled, showFileSize, allowRetry, size],
    );

    // Derive form control helper and error text
    const derivedError =
      errorText ||
      (uploadState.hasErrors && !uploadState.hasUploading ? FILE_UPLOAD_CONSTANTS.ERRORS.MULTIPLE_FAILURES : undefined);
    // Show helper copy only; inline errors are handled via derivedError for red borders
    const derivedHelper = helpText;

    const isInvalid = !!derivedError;

    return (
      <CustomFormControl
        id={id}
        size={size}
        controlProps={{ isInvalid, mb: 0 }}
        label={
          label ? (
            <Box>
              {label}
              {isOptional && (
                <Box as="span" color="muted">
                  {' '}
                  {FILE_UPLOAD_CONSTANTS.OPTIONAL_TEXT}
                </Box>
              )}
            </Box>
          ) : undefined
        }
        labelProps={{ as: 'legend' }}
        helperText={derivedHelper}
        error={derivedError}
      >
        {/* Drop zone with dynamic border color based on upload state - files appear inside */}
        <Box
          {...dropZoneProps}
          bgColor={'chakra-subtle-bg'}
          borderStyle={'dashed'}
          borderWidth={2}
          px={8}
          py={6}
          borderRadius={'xl'}
          borderColor={getContainerBorderColor(isInvalid)}
          cursor={isDisabled ? 'not-allowed' : undefined}
          opacity={isDisabled ? 0.6 : undefined}
          mb={4}
          position={'relative'}
          overflow={'hidden'}
          __css={{
            ...styles.dropzone,
          }}
        >
          {/* Regular content */}
          <Flex gap={4} alignItems={'center'}>
            <Button
              variant={'solid'}
              size="lg"
              onClick={() => fileInputRef.current?.click()}
              isDisabled={isDisabled || uploadState.isActive}
            >
              {uploadState.isActive ? FILE_UPLOAD_CONSTANTS.FILES_UPLOADING : FILE_UPLOAD_CONSTANTS.CHOOSE_FILES_LABEL}
            </Button>

            {allowDragDrop && (
              <Box color={'muted'}>
                {uploadState.isActive ? 'Upload in progress - please wait' : FILE_UPLOAD_CONSTANTS.DRAG_DROP_TEXT}
              </Box>
            )}
          </Flex>

          {/* Drag overlay matching Figma design exactly */}
          {isDragging && (
            <Center
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bgColor="blackAlpha.800"
              color={'white'}
              flexDirection="column"
              data-selector="drag-overlay"
              role="region"
            >
              <Icon icon={faCloudUpload} fontSize={'2xl'} color="whiteAlpha.700" fixedWidth />

              <Box as="span" fontSize="md" fontWeight="normal" mb={3}>
                {FILE_UPLOAD_CONSTANTS.DROP_FILES_TEXT}
              </Box>
            </Center>
          )}

          <VisuallyHiddenInput
            ref={fileInputRef}
            type="file"
            multiple={allowMultiple}
            accept={acceptedFileTypes.join(',')}
            onChange={handleFileInputChange}
            disabled={isDisabled}
            aria-label={FILE_UPLOAD_CONSTANTS.ARIA_LABELS.FILE_INPUT}
          />

          {/* File list inside the drop zone */}
          {files.length > 0 && <Box mt={4}>{fileList}</Box>}
        </Box>
      </CustomFormControl>
    );
  },
);

FileUpload.displayName = 'FileUpload';

export default FileUpload;
